import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import FlightService from '../../../services/FlightService';
import ClientService from '../../../services/ClientService';
import SimpleSearchableClientDropdown from '../../../components/client/SimpleSearchableClientDropdown';

export type ShopifyPilotRunOptions = {
  skipInventory: boolean;
  loadFullInventory: boolean;
};

export type ShopifyPilotJobSettingsProps = {
  historySelected: boolean;
  options: ShopifyPilotRunOptions;
  onChange: (next: ShopifyPilotRunOptions) => void;
  client: any;
  tipContext: any;
};

const CONFIRM_MESSAGE =
  'This will delete this client, update all raw data to point to the new client, and update the new client to be a ShopifyPilot flight client. This will remove the target client from their old pos integration';

const MERGEABLE_PURCHASE_ORDER_SYSTEMS = ['shopify', 'stocky', 'faves'];
const INVENTORY_PRODUCT_STATUSES = ['ACTIVE', 'UNLISTED', 'DRAFT', 'ARCHIVED'] as const;

const ShopifyPilotJobSettings: React.FC<ShopifyPilotJobSettingsProps> = ({
  historySelected,
  options,
  onChange,
  client,
  tipContext,
}) => {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedTargetClient, setSelectedTargetClient] = useState<any | null>(null);
  const [migrateRunning, setMigrateRunning] = useState(false);
  const [migrateError, setMigrateError] = useState<string>('');
  const [migrateModalOpen, setMigrateModalOpen] = useState(false);
  const [migrateBrokenSteps, setMigrateBrokenSteps] = useState<string[]>([]);

  // Location override modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string>('');
  const [settingsLoadedForClientId, setSettingsLoadedForClientId] = useState<number | null>(null);
  const [allSalesChannels, setAllSalesChannels] = useState<string[]>([]);

  type OverrideRow = { id: string; salesChannel: string; overrideLocationId: string };

  const [overrideRows, setOverrideRows] = useState<OverrideRow[]>([]);

  // Copy structure modal state
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyRunning, setCopyRunning] = useState(false);
  const [copyError, setCopyError] = useState<string>('');
  const [selectedSourceClient, setSelectedSourceClient] = useState<any | null>(null);

  // Merge actuals modal state
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeRunning, setMergeRunning] = useState(false);
  const [mergeError, setMergeError] = useState<string>('');
  // Merge modal checkboxes
  const [mergeActuals, setMergeActuals] = useState(true);
  const [mergeOnOrder, setMergeOnOrder] = useState(false);
  const [mergeOnOrderLastMonth, setMergeOnOrderLastMonth] = useState(false);
  const [mergeReceiving, setMergeReceiving] = useState(false);
  const [mergeIncludeHardMarkdowns, setMergeIncludeHardMarkdowns] = useState(false);
  const [mergeModeHistory, setMergeModeHistory] = useState(false); // false = current month, true = history

  // Default: current month's first day for start, same for end
  const [mergeStartMonth, setMergeStartMonth] = useState<Moment>(moment().subtract(1, 'month').startOf('month'));
  const [mergeEndMonth, setMergeEndMonth] = useState<Moment>(moment().subtract(1, 'month').startOf('month'));

  // Advanced run modal state
  type AdvancedRunState = {
    open: boolean;
    running: boolean;
    error: string;
    period: Moment;
    steps: { products: boolean; inventory: boolean; orders: boolean; transfers: boolean; purchaseOrders: boolean };
    merge: { actuals: boolean; includeHardMarkdowns: boolean; onOrder: boolean; onOrderLastMonth: boolean; receiving: boolean };
  };
  const ADVANCED_RUN_DEFAULTS: AdvancedRunState = {
    open: false, running: false, error: '', period: moment(),
    steps: { products: true, inventory: true, orders: true, transfers: true, purchaseOrders: false },
    merge: { actuals: false, includeHardMarkdowns: false, onOrder: false, onOrderLastMonth: false, receiving: false },
  };
  const [advancedRun, setAdvancedRun] = useState<AdvancedRunState>(ADVANCED_RUN_DEFAULTS);
  const patchAdvanced = (patch: Partial<AdvancedRunState>) => setAdvancedRun(prev => ({ ...prev, ...patch }));
  const patchAdvancedSteps = (patch: Partial<AdvancedRunState['steps']>) =>
    setAdvancedRun(prev => ({ ...prev, steps: { ...prev.steps, ...patch } }));
  const patchAdvancedMerge = (patch: Partial<AdvancedRunState['merge']>) =>
    setAdvancedRun(prev => ({ ...prev, merge: { ...prev.merge, ...patch } }));

  // Advanced settings modal state
  const [advSettingsOpen, setAdvSettingsOpen] = useState(false);
  const [advSettingsLoading, setAdvSettingsLoading] = useState(false);
  const [advSettingsSaving, setAdvSettingsSaving] = useState(false);
  const [advSettingsError, setAdvSettingsError] = useState<string>('');
  const [advShopifyDomain, setAdvShopifyDomain] = useState<string>('');
  const [advAutoMerge, setAdvAutoMerge] = useState<boolean>(false);
  const [advIncludeHardMarkdowns, setAdvIncludeHardMarkdowns] = useState<boolean>(false);
  const [advInventoryProductStatuses, setAdvInventoryProductStatuses] = useState<string[]>([...INVENTORY_PRODUCT_STATUSES]);
  const [advPurchaseOrderSystem, setAdvPurchaseOrderSystem] = useState<string>('');
  const [advStoreName, setAdvStoreName] = useState<string>('');
  const [advStockyApiKey, setAdvStockyApiKey] = useState<string>('');

  // The client's current PO system — loaded once per client, used to seed modal dropdowns
  const [poSystem, setPoSystem] = useState<string>('');

  // The client's persisted "include hard markdowns" advanced setting — loaded once per client,
  // used to seed the per-run hard markdowns checkboxes in the merge and advanced run modals.
  const [hardMarkdownsDefault, setHardMarkdownsDefault] = useState<boolean>(false);

  // Per-modal PO system selections (seeded from poSystem when modal opens)
  const [mergePOSystem, setMergePOSystem] = useState<string>('');
  const [advRunPOSystem, setAdvRunPOSystem] = useState<string>('');

  const normalizeInventoryProductStatuses = (raw: any): string[] => {
    if (!Array.isArray(raw)) return [...INVENTORY_PRODUCT_STATUSES];
    const normalized = raw
      .map((value) => String(value ?? '').trim().toUpperCase())
      .filter((value) => INVENTORY_PRODUCT_STATUSES.includes(value as any));
    const unique = Array.from(new Set(normalized));
    return unique.length ? unique : [...INVENTORY_PRODUCT_STATUSES];
  };

  const toggleInventoryProductStatus = (status: string) => {
    setAdvInventoryProductStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const selectAllInventoryProductStatuses = () => {
    setAdvInventoryProductStatuses([...INVENTORY_PRODUCT_STATUSES]);
  };

  const clearInventoryProductStatuses = () => {
    setAdvInventoryProductStatuses([]);
  };

  const normalizePurchaseOrderSystem = (value: string) => value.trim().toLowerCase();

  const isMergeablePurchaseOrderSystem = (value: string) =>
    MERGEABLE_PURCHASE_ORDER_SYSTEMS.includes(normalizePurchaseOrderSystem(value));

  useEffect(() => {
    let mounted = true;

    const loadClients = async () => {
      try {
        const res = await ClientService.listClientsSimple();
        // API returns { result: [...] } in most places; fallback for other shapes
        const list = (res?.result ?? res) as any[];
        if (mounted) setClients(Array.isArray(list) ? list : []);
      } catch (e: any) {
        // keep UI usable even if dropdown fails
        if (mounted) setClients([]);
      }
    };

    loadClients();

    return () => {
      mounted = false;
    };
  }, []);

  // Load PO system whenever the client changes so modals can default to it
  useEffect(() => {
    if (!client?.Id) return;
    let mounted = true;
    FlightService.getShopifyAdvancedSettings(client.Id)
      .then((res: any) => {
        if (!mounted) return;
        const d = res?.result ?? res;
        setPoSystem(d?.purchase_order_system ?? '');
        setHardMarkdownsDefault(!!d?.include_hard_markdowns);
        setAdvInventoryProductStatuses(normalizeInventoryProductStatuses(d?.inventory_product_statuses));
      })
      .catch(() => { /* non-fatal — modals will still let user pick manually */ });
    return () => { mounted = false; };
  }, [client?.Id]);

  useEffect(() => {
    // reset state when the source client changes
    setSelectedTargetClient(null);
    setMigrateRunning(false);
    setMigrateError('');
    setMigrateModalOpen(false);
    setMigrateBrokenSteps([]);

    // reset settings modal state
    setSettingsModalOpen(false);
    setSettingsLoading(false);
    setSettingsSaving(false);
    setSettingsError('');
    setOverrideRows([]);
    setSettingsLoadedForClientId(null);
    setAllSalesChannels([]);

    // reset copy structure modal state
    setCopyModalOpen(false);
    setCopyRunning(false);
    setCopyError('');
    setSelectedSourceClient(null);

    // reset merge actuals modal state
    setMergeModalOpen(false);
    setMergeRunning(false);
    setMergeError('');
    setMergeActuals(true);
    setMergeOnOrder(false);
    setMergeOnOrderLastMonth(false);
    setMergeReceiving(false);
    setMergeIncludeHardMarkdowns(false);
    setMergeStartMonth(moment().subtract(1, 'month').startOf('month'));
    setMergeEndMonth(moment().subtract(1, 'month').startOf('month'));
    setMergeModeHistory(false);

    // reset advanced run modal state
    setAdvancedRun({ ...ADVANCED_RUN_DEFAULTS, period: moment() });

    // reset advanced settings modal state
    setAdvSettingsOpen(false);
    setAdvSettingsLoading(false);
    setAdvSettingsSaving(false);
    setAdvSettingsError('');
    setAdvShopifyDomain('');
    setAdvAutoMerge(false);
    setAdvIncludeHardMarkdowns(false);
    setAdvInventoryProductStatuses([...INVENTORY_PRODUCT_STATUSES]);
    setAdvPurchaseOrderSystem('');
    setAdvStoreName('');
    setAdvStockyApiKey('');
    setPoSystem('');
    setHardMarkdownsDefault(false);
    setMergePOSystem('');
    setAdvRunPOSystem('');
  }, [client?.Id]);

  const filteredClients = useMemo(() => {
    // Avoid allowing selecting the same client as target
    const sourceId = client?.Id;
    return clients.filter((c) => c?.Id !== sourceId);
  }, [clients, client?.Id]);

  const openAdvancedSettings = async () => {
    if (!client?.Id) return;
    setAdvSettingsError('');
    setAdvSettingsOpen(true);
    setAdvSettingsLoading(true);
    try {
      const res = await FlightService.getShopifyAdvancedSettings(client.Id);
      const d = res?.result ?? res;
      const pos = d?.purchase_order_system ?? '';
      setAdvShopifyDomain(d?.shopify_domain ?? '');
      setAdvAutoMerge(!!d?.auto_merge_actuals);
      setAdvIncludeHardMarkdowns(!!d?.include_hard_markdowns);
      setAdvInventoryProductStatuses(normalizeInventoryProductStatuses(d?.inventory_product_statuses));
      setAdvPurchaseOrderSystem(pos);
      setAdvStoreName(d?.store_name ?? '');
      setAdvStockyApiKey(d?.stocky_api_key ?? '');
      // Keep root poSystem in sync
      setPoSystem(pos);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to load advanced settings';
      setAdvSettingsError(msg);
    } finally {
      setAdvSettingsLoading(false);
    }
  };

  const onSaveAdvancedSettings = async () => {
    if (advSettingsSaving || !client?.Id) return;
    setAdvSettingsError('');

    const payload: Record<string, any> = {
      store_name: advStoreName.trim() || null,
      auto_merge_actuals: advAutoMerge,
      include_hard_markdowns: advIncludeHardMarkdowns,
      inventory_product_statuses: advInventoryProductStatuses,
      purchase_order_system: advPurchaseOrderSystem || null,
    };

    if (advPurchaseOrderSystem === 'stocky') {
      payload.stocky_api_key = advStockyApiKey.trim() || null;
    }
    try {
      setAdvSettingsSaving(true);
      await FlightService.updateShopifyAdvancedSettings(client.Id, payload);
      tipContext?.showSuccess?.('Advanced settings saved.', 8);
      // Keep root poSystem in sync so modals immediately reflect the new value
      setPoSystem(advPurchaseOrderSystem);
      setAdvSettingsOpen(false);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to save advanced settings';
      setAdvSettingsError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setAdvSettingsSaving(false);
    }
  };

  const onMigrate = async () => {
    if (migrateRunning) return;
    setMigrateError('');
    setMigrateBrokenSteps([]);

    if (!client?.Id) {
      setMigrateError('Missing source client id');
      return;
    }

    if (!selectedTargetClient?.Id) {
      setMigrateError('Please select a target client.');
      return;
    }

    const confirmed = window.confirm(CONFIRM_MESSAGE);
    if (!confirmed) return;

    try {
      setMigrateRunning(true);
      tipContext?.showSuccess?.('Starting Shopify migration. This may take a bit...', 8);

      const res = await FlightService.migrateShopifyClientToRealClient(client.Id, selectedTargetClient.Id);
      // If response has failedSteps, show them in UI
      if (res && Array.isArray(res.failedSteps) && res.failedSteps.length > 0) {
        setMigrateBrokenSteps(res.failedSteps);
        tipContext?.showSuccess?.('Shopify migration completed with some issues.', 10);
      } else {
        setMigrateBrokenSteps([]);
        tipContext?.showSuccess?.('Shopify migration started/successful.', 10);
        setMigrateModalOpen(false);
      }
    } catch (error: any) {
      // DisplayableException returns { message: string } (safe to display verbatim)
      const msg = error?.response?.data?.message || error?.message || 'Migration failed';
      setMigrateError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setMigrateRunning(false);
    }
  };

  const openLocationOverridesModal = async () => {
    if (!client?.Id) {
      tipContext?.show?.('Missing client id', 8);
      return;
    }

    // Always start from a clean slate and force a refresh each open
    setOverrideRows([]);
    setSettingsError('');
    setSettingsLoadedForClientId(null);
    setAllSalesChannels([]);

    setSettingsModalOpen(true);

    setSettingsLoading(true);
    try {
      const res = await FlightService.getShopifyLocationOverrides(client.Id);

      const overridesRaw = (res?.overrides ?? res?.result ?? res) as any[];
      const salesChannelsRaw = Array.isArray(res?.salesChannels) ? (res.salesChannels as string[]) : [];

      const normalized: OverrideRow[] = Array.isArray(overridesRaw)
        ? overridesRaw.map((r: any, idx: number) => ({
            id: `${client.Id}-${String(r?.SALES_CHANNEL ?? idx)}-${idx}-${Date.now()}`,
            salesChannel: String(r?.SALES_CHANNEL ?? ''),
            overrideLocationId:
              r?.OVERRIDE_LOCATION_ID === null || typeof r?.OVERRIDE_LOCATION_ID === 'undefined'
                ? ''
                : String(r?.OVERRIDE_LOCATION_ID),
          }))
        : [];

      normalized.sort((a, b) => a.salesChannel.localeCompare(b.salesChannel));

      setOverrideRows(normalized);
      setAllSalesChannels(salesChannelsRaw);
      setSettingsLoadedForClientId(client.Id);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to load Shopify Pilot settings';
      setSettingsError(msg);
      tipContext?.show?.(msg, 10);
    } finally {
      setSettingsLoading(false);
    }
  };

  const closeLocationOverridesModal = () => {
    // Do not allow closing while a save is running
    if (settingsSaving) return;

    setSettingsModalOpen(false);

    // Wipe modal contents so it always reloads fresh next open
    setOverrideRows([]);
    setSettingsError('');
    setSettingsLoading(false);
    setSettingsLoadedForClientId(null);
    setAllSalesChannels([]);
  };

  const updateRow = (id: string, patch: Partial<Pick<OverrideRow, 'salesChannel' | 'overrideLocationId'>>) => {
    setOverrideRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    const id = `new-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setOverrideRows((prev) => [...prev, { id, salesChannel: '', overrideLocationId: '' }]);
  };

  const removeRow = (id: string) => {
    setOverrideRows((prev) => prev.filter((r) => r.id !== id));
  };

  const onSaveLocationOverrides = async () => {
    if (settingsSaving) return;
    if (!client?.Id) {
      setSettingsError('Missing client id');
      return;
    }

    setSettingsError('');

    // Validate sales channel uniqueness and presence
    const cleaned = overrideRows
      .map((r) => ({
        salesChannel: (r.salesChannel ?? '').trim(),
        overrideLocationId: (r.overrideLocationId ?? '').trim(),
      }))
      .filter((r) => r.salesChannel.length > 0);

    const dupes = cleaned
      .map((r) => r.salesChannel)
      .filter((sc, i, arr) => arr.indexOf(sc) !== i);

    if (dupes.length) {
      setSettingsError(`Duplicate SALES_CHANNEL values: ${Array.from(new Set(dupes)).join(', ')}`);
      return;
    }

    const overrides: Record<string, number | null> = {};
    for (const row of cleaned) {
      // Empty override implies clearing it (NULL)
      if (row.overrideLocationId === '') {
        overrides[row.salesChannel] = null;
        continue;
      }

      const asNum = Number(row.overrideLocationId);
      if (!Number.isFinite(asNum) || !Number.isInteger(asNum)) {
        setSettingsError(`OVERRIDE_LOCATION_ID must be an integer for SALES_CHANNEL '${row.salesChannel}'`);
        return;
      }
      overrides[row.salesChannel] = asNum;
    }

    try {
      setSettingsSaving(true);
      tipContext?.showSuccess?.('Saving Shopify Pilot location overrides...', 6);
      await FlightService.saveShopifyLocationOverrides(client.Id, overrides);
      tipContext?.showSuccess?.('Shopify Pilot settings saved.', 8);
      setSettingsModalOpen(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to save Shopify Pilot settings';
      setSettingsError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadAllSalesChannelRows = () => {
    setOverrideRows((prev) => {
      const existingChannels = new Set(prev.map((r) => r.salesChannel));
      const missing = allSalesChannels.filter((sc) => !existingChannels.has(sc));
      if (missing.length === 0) return prev;
      const newRows: OverrideRow[] = missing.map((sc) => ({
        id: `new-${sc}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        salesChannel: sc,
        overrideLocationId: '',
      }));
      const merged = [...prev, ...newRows];
      merged.sort((a, b) => a.salesChannel.localeCompare(b.salesChannel));
      return merged;
    });
  };

  const onCopyStructure = async () => {
    if (copyRunning) return;
    setCopyError('');

    if (!client?.Id) {
      setCopyError('Missing destination client id');
      return;
    }

    if (!selectedSourceClient?.Id) {
      setCopyError('Please select a source client.');
      return;
    }

    if (selectedSourceClient.Id === client.Id) {
      setCopyError('Source client must be different from the destination client.');
      return;
    }

    const confirmed = window.confirm(
      `This will copy locations, departments, classes, and location classes from '${selectedSourceClient?.Name ?? selectedSourceClient?.ClientName ?? selectedSourceClient?.Id}' onto the current client.\n\nIt will fail if the current client already has any classes. Continue?`
    );
    if (!confirmed) return;

    try {
      setCopyRunning(true);
      tipContext?.showSuccess?.('Copying client structure...', 6);
      await FlightService.copyClientStructureFromSource(client.Id, selectedSourceClient.Id);
      tipContext?.showSuccess?.('Client structure copied successfully.', 8);
      setCopyModalOpen(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Copy failed';
      setCopyError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setCopyRunning(false);
    }
  };

  const onMergeActuals = async () => {
    if (mergeRunning) return;
    setMergeError('');

    if (!client?.Id) {
      setMergeError('Missing client id');
      return;
    }

    if (!mergeActuals && !mergeOnOrder && !mergeOnOrderLastMonth && !mergeReceiving) {
      setMergeError('Please select at least one merge type.');
      return;
    }

    const normalizedMergePOSystem = normalizePurchaseOrderSystem(mergePOSystem);

    if ((mergeOnOrder || mergeOnOrderLastMonth || mergeReceiving) && !normalizedMergePOSystem) {
      setMergeError('Please select a purchase order system for purchase-order merges.');
      return;
    }

    if ((mergeOnOrder || mergeOnOrderLastMonth || mergeReceiving) && !isMergeablePurchaseOrderSystem(normalizedMergePOSystem)) {
      setMergeError(`Purchase order system '${mergePOSystem || 'none'}' does not support purchase-order merges.`);
      return;
    }

    if (mergeOnOrderLastMonth && normalizedMergePOSystem !== 'shopify') {
      setMergeError('Last month on-order merge is currently supported only for Shopify purchase-order system.');
      return;
    }

    try {
      setMergeRunning(true);

      if (mergeModeHistory) {
        // History mode — requires date range
        if (!mergeStartMonth || !mergeEndMonth) {
          setMergeError('Please select a start and end month.');
          setMergeRunning(false);
          return;
        }
        const startKey = mergeStartMonth.format('YYYY-MM');
        const endKey = mergeEndMonth.format('YYYY-MM');
        if (startKey > endKey) {
          setMergeError('Start month must be on or before end month.');
          setMergeRunning(false);
          return;
        }
        const startDate = `${startKey}-01`;
        const endDate = `${endKey}-01`;

        if (mergeActuals) {
          tipContext?.showSuccess?.('Merging Shopify actuals (history). This may take several minutes...', 10);
          await FlightService.mergeShopifyActualsHistory(client.Id, startDate, endDate, mergeIncludeHardMarkdowns);
        }
        if (mergeReceiving) {
          tipContext?.showSuccess?.(`Merging ${normalizedMergePOSystem} receiving (history)...`, 10);
          await FlightService.mergePurchaseOrderReceiving(client.Id, normalizedMergePOSystem, startDate, endDate);
        }
        // On Order is not available in history mode
      } else {
        // Current month mode
        const now = moment();
        const startDate = now.clone().startOf('month').format('YYYY-MM-DD');
        const endDate = now.clone().endOf('month').format('YYYY-MM-DD');

        if (mergeActuals) {
          tipContext?.showSuccess?.('Merging Shopify actuals (current month). This may take several minutes...', 10);
          await FlightService.mergeShopifyActualsCurrentMonth(client.Id, mergeIncludeHardMarkdowns);
        }
        if (mergeOnOrder) {
          tipContext?.showSuccess?.(`Merging ${normalizedMergePOSystem} on-orders...`, 10);
          await FlightService.mergePurchaseOrderOnOrder(client.Id, normalizedMergePOSystem);
        }
        if (mergeOnOrderLastMonth) {
          tipContext?.showSuccess?.(`Merging ${normalizedMergePOSystem} on-orders (last month backdated)...`, 10);
          await FlightService.mergePurchaseOrderOnOrder(client.Id, normalizedMergePOSystem, 'last_month');
        }
        if (mergeReceiving) {
          tipContext?.showSuccess?.(`Merging ${normalizedMergePOSystem} receiving (current month)...`, 10);
          await FlightService.mergePurchaseOrderReceiving(client.Id, normalizedMergePOSystem, startDate, endDate);
        }
      }

      tipContext?.showSuccess?.('All selected merges completed successfully.', 10);
      setMergeModalOpen(false);
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Merge failed';
      setMergeError(msg);
      tipContext?.show?.(msg, 12);
    } finally {
      setMergeRunning(false);
    }
  };

  const onAdvancedRun = async () => {
    if (advancedRun.running) return;
    patchAdvanced({ error: '' });

    if (!client?.Id) {
      patchAdvanced({ error: 'Missing client id' });
      return;
    }

    const steps: string[] = [];
    if (advancedRun.steps.products) steps.push('products');
    if (advancedRun.steps.inventory) steps.push('inventory');
    if (advancedRun.steps.orders) steps.push('orders');
    if (advancedRun.steps.transfers) steps.push('transfers');
    if (advancedRun.steps.purchaseOrders) steps.push('purchase_orders');

    const isCurrentMonth = advancedRun.period.isSame(moment(), 'month');
    const normalizedAdvRunPOSystem = normalizePurchaseOrderSystem(advRunPOSystem);
    const mergeOptions = {
      actuals: advancedRun.merge.actuals,
      includeHardMarkdowns: advancedRun.merge.includeHardMarkdowns,
      onOrder: advancedRun.merge.onOrder && isCurrentMonth,
      onOrderLastMonth: advancedRun.merge.onOrderLastMonth && isCurrentMonth,
      receiving: advancedRun.merge.receiving,
      purchaseOrderSystem: normalizedAdvRunPOSystem,
    };
    const hasMerge = mergeOptions.actuals || mergeOptions.onOrder || mergeOptions.onOrderLastMonth || mergeOptions.receiving;

    if (steps.length === 0 && !hasMerge) {
      patchAdvanced({ error: 'Please select at least one step or merge operation to run.' });
      return;
    }

    if ((mergeOptions.onOrder || mergeOptions.onOrderLastMonth || mergeOptions.receiving) && !normalizedAdvRunPOSystem) {
      patchAdvanced({ error: 'Please select a purchase order system for purchase-order merges.' });
      return;
    }

    if ((mergeOptions.onOrder || mergeOptions.onOrderLastMonth || mergeOptions.receiving) && !isMergeablePurchaseOrderSystem(normalizedAdvRunPOSystem)) {
      patchAdvanced({ error: `Purchase order system '${advRunPOSystem || 'none'}' does not support purchase-order merges.` });
      return;
    }

    if (mergeOptions.onOrderLastMonth && normalizedAdvRunPOSystem !== 'shopify') {
      patchAdvanced({ error: 'Last month on-order merge is currently supported only for Shopify purchase-order system.' });
      return;
    }

    const period = advancedRun.period.format('YYYYMM');

    try {
      patchAdvanced({ running: true });
      tipContext?.showSuccess?.(`Starting advanced run. This may take several minutes...`, 10);
      await FlightService.runShopifyPilotAdvanced(client.Id, period, steps, mergeOptions);
      tipContext?.showSuccess?.('Advanced run completed successfully.', 10);
      patchAdvanced({ open: false });
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Advanced run failed';
      patchAdvanced({ error: msg });
      tipContext?.show?.(msg, 12);
    } finally {
      patchAdvanced({ running: false });
    }
  };

  return (
    <>
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.skipInventory}
            onChange={(e) => onChange({ ...options, skipInventory: e.target.checked })}
            color="primary"
          />
        }
        label="Skip inventory load? (heavy usage, don't run for history loads if its already been done)"
      />
      <FormControlLabel
        sx={{ ml: 1 }}
        control={
          <Checkbox
            checked={!!options.loadFullInventory}
            disabled={!!historySelected}
            onChange={(e) => onChange({ ...options, loadFullInventory: e.target.checked })}
            color="primary"
          />
        }
        label="Load full inventory? (only for first time loads)"
      />

      {/* Shopify Pilot tools */}
      <div className="mt-4 border-t border-gray-300 pt-4 px-1">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            onClick={openLocationOverridesModal}
            disabled={settingsLoading || settingsSaving}
          >
            Location override settings
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            onClick={() => setCopyModalOpen(true)}
            disabled={copyRunning}
          >
            Copy client structure
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            onClick={() => { setMergePOSystem(poSystem); setMergeIncludeHardMarkdowns(hardMarkdownsDefault); setMergeModalOpen(true); }}
            disabled={mergeRunning}
          >
            Merge Actuals
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
            onClick={() => setMigrateModalOpen(true)}
            disabled={migrateRunning}
          >
            Open Migration Tool
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-gray-400 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 disabled:opacity-50"
            onClick={openAdvancedSettings}
            disabled={advSettingsLoading || advSettingsSaving}
          >
            Advanced Settings
          </button>

          <button
            type="button"
            className="inline-flex items-center rounded-md border border-blue-400 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
            onClick={() => {
              setAdvRunPOSystem(poSystem);
              patchAdvanced({ open: true });
              patchAdvancedMerge({ includeHardMarkdowns: hardMarkdownsDefault });
            }}
            disabled={advancedRun.running}
          >
            Advanced Run
          </button>

          {settingsLoading ? (
            <span className="inline-flex items-center gap-2 text-sm text-gray-600">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
              Loading...
            </span>
          ) : null}
        </div>

        {advSettingsOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => (!advSettingsSaving ? setAdvSettingsOpen(false) : undefined)}
            />
            <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-base font-semibold">Advanced Settings</div>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => setAdvSettingsOpen(false)}
                  disabled={advSettingsSaving}
                >
                  Close
                </button>
              </div>

              {advSettingsLoading ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                  Loading...
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  {/* Store name / URL — always shown; backend derives shopify_domain from this */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Store Name (URL)</label>
                    <input
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      placeholder="e.g. https://my-store.myshopify.com"
                      value={advStoreName}
                      onChange={(e) => setAdvStoreName(e.target.value)}
                      disabled={advSettingsSaving}
                    />
                  </div>

                  {/* Auto merge */}
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={advAutoMerge}
                          onChange={(e) => setAdvAutoMerge(e.target.checked)}
                          disabled={advSettingsSaving}
                        />
                      }
                      label={<span className="text-sm">Auto merge actuals after each flight</span>}
                    />
                  </div>

                  {/* Include hard markdowns */}
                  <div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={advIncludeHardMarkdowns}
                          onChange={(e) => setAdvIncludeHardMarkdowns(e.target.checked)}
                          disabled={advSettingsSaving}
                        />
                      }
                      label={<span className="text-sm">Include hard markdowns in auto-merged actuals</span>}
                    />
                  </div>

                  {/* Inventory statuses to include in inventory merge */}
                  <div>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <label className="block text-xs font-semibold text-gray-600">Inventory Product Statuses to Include</label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-xs font-medium text-blue-700 hover:text-blue-800 disabled:opacity-50"
                          onClick={selectAllInventoryProductStatuses}
                          disabled={advSettingsSaving}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="text-xs font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
                          onClick={clearInventoryProductStatuses}
                          disabled={advSettingsSaving}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="rounded-md border border-gray-300 bg-gray-50 p-2">
                      <div className="flex flex-wrap gap-2">
                        {INVENTORY_PRODUCT_STATUSES.map((status) => {
                          const selected = advInventoryProductStatuses.includes(status);
                          return (
                            <button
                              key={status}
                              type="button"
                              aria-pressed={selected}
                              onClick={() => toggleInventoryProductStatus(status)}
                              disabled={advSettingsSaving}
                              className={[
                                'inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50',
                                selected
                                  ? 'border-blue-600 bg-blue-600 text-white'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100',
                              ].join(' ')}
                            >
                              {status}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Leave empty to include all statuses. Products with NULL status are always included.
                    </div>
                  </div>

                  {/* Purchase order system */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Purchase Order System</label>
                    <select
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={advPurchaseOrderSystem}
                      onChange={(e) => setAdvPurchaseOrderSystem(e.target.value)}
                      disabled={advSettingsSaving}
                    >
                      <option value="">— None —</option>
                      <option value="shopify">Shopify</option>
                      <option value="stocky">Stocky</option>
                      <option value="faves">Faves</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Stocky API key — only shown when stocky is selected */}
                  {advPurchaseOrderSystem === 'stocky' ? (
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Stocky Auth</div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">API Key (Access Token)</label>
                        <input
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          placeholder="..."
                          value={advStockyApiKey}
                          onChange={(e) => setAdvStockyApiKey(e.target.value)}
                          disabled={advSettingsSaving}
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="flex items-center justify-end gap-3 pt-1">
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                      disabled={advSettingsSaving}
                      onClick={onSaveAdvancedSettings}
                    >
                      {advSettingsSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}

              {advSettingsError ? <div className="mt-2 text-sm text-red-600">{advSettingsError}</div> : null}
            </div>
          </div>
        ) : null}

        {copyModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => (!copyRunning ? setCopyModalOpen(false) : undefined)}
            />
            <div className="relative z-10 w-full max-w-xl rounded-lg bg-white p-4 shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-base font-semibold">Copy Client Structure</div>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => setCopyModalOpen(false)}
                  disabled={copyRunning}
                >
                  Close
                </button>
              </div>

              <div className="mt-3 text-sm text-gray-800">
                Select a <span className="font-semibold">source</span> client to copy locations, departments, classes, and location classes onto the current client.
                This will <span className="font-semibold">fail</span> if the current client already has any classes.
                This may take several minutes, please leave this page open to see if any errors occur during run.
                You may use RO on other tabs without issue.
              </div>

              <div className="mt-4">
                <SimpleSearchableClientDropdown
                  clients={filteredClients}
                  selectedClient={selectedSourceClient}
                  setSelectedClient={setSelectedSourceClient}
                  styleOverrides={{ width: '100%' }}
                />
              </div>

              {copyRunning ? (
                <div className="mt-4 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                    Copying...
                  </span>
                </div>
              ) : null}

              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={copyRunning || !selectedSourceClient?.Id}
                  onClick={onCopyStructure}
                >
                  {copyRunning ? 'Copying...' : 'Copy'}
                </button>
              </div>

              {copyError ? <div className="mt-2 text-sm text-red-600">{copyError}</div> : null}
            </div>
          </div>
        ) : null}

        {settingsModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => (!settingsSaving ? closeLocationOverridesModal() : undefined)}
            />
            <div className="relative z-10 w-full max-w-3xl rounded-lg bg-white p-4 shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-base font-semibold">Shopify Pilot Settings</div>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={closeLocationOverridesModal}
                  disabled={settingsSaving}
                >
                  Close
                </button>
              </div>

              <div className="mt-2 text-sm text-gray-700">
                Edit mappings of <span className="font-mono">SALES_CHANNEL</span> → <span className="font-mono">OVERRIDE_LOCATION_ID</span>.
                Leaving override blank will clear it.
              </div>

              {settingsLoading ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                  Loading current settings...
                </div>
              ) : (
                <>
                  {allSalesChannels.length > 0 ? (
                    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-xs font-semibold text-gray-600">Distinct Sales Channels on Raw Data</div>
                        <button
                          type="button"
                          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-50"
                          onClick={loadAllSalesChannelRows}
                          disabled={settingsSaving}
                        >
                          Load All
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {allSalesChannels.map((sc) => {
                          const mapped = overrideRows.some((r) => r.salesChannel === sc);
                          return (
                            <span
                              key={sc}
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                mapped
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {sc}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-4 max-h-[45vh] overflow-y-auto rounded-md border border-gray-200">
                    <div className="grid grid-cols-12 gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
                      <div className="col-span-5">SALES_CHANNEL</div>
                      <div className="col-span-5">OVERRIDE_LOCATION_ID</div>
                      <div className="col-span-2 text-right">Actions</div>
                    </div>

                    <div className="p-3">
                      {overrideRows.length === 0 ? (
                        <div className="text-sm text-gray-500">No overrides yet. Add one below.</div>
                      ) : null}

                      <div className="space-y-2">
                        {overrideRows.map((row) => (
                          <div key={row.id} className="grid grid-cols-12 gap-2">
                            <input
                              className="col-span-5 rounded-md border border-gray-300 px-2 py-2 text-sm"
                              placeholder="e.g. Online Store"
                              value={row.salesChannel}
                              onChange={(e) => updateRow(row.id, { salesChannel: e.target.value })}
                              disabled={settingsSaving}
                            />
                            <input
                              className="col-span-5 rounded-md border border-gray-300 px-2 py-2 text-sm"
                              placeholder="e.g. 123456789"
                              value={row.overrideLocationId}
                              onChange={(e) => updateRow(row.id, { overrideLocationId: e.target.value })}
                              disabled={settingsSaving}
                              inputMode="numeric"
                            />
                            <div className="col-span-2 flex justify-end">
                              <button
                                type="button"
                                className="rounded-md px-2 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                                onClick={() => removeRow(row.id)}
                                disabled={settingsSaving}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:opacity-50"
                      onClick={addRow}
                      disabled={settingsSaving}
                    >
                      Add Mapping
                    </button>

                    <button
                      type="button"
                      className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                      onClick={onSaveLocationOverrides}
                      disabled={settingsSaving}
                    >
                      {settingsSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </>
              )}

              {settingsError ? <div className="mt-2 text-sm text-red-600">{settingsError}</div> : null}
            </div>
          </div>
        ) : null}

        {mergeModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => (!mergeRunning ? setMergeModalOpen(false) : undefined)}
            />
            <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-4 shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-base font-semibold">Merge Shopify Data</div>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => setMergeModalOpen(false)}
                  disabled={mergeRunning}
                >
                  Close
                </button>
              </div>

              <div className="mt-3 text-sm text-gray-800">
                Select which merge operations to run. This may take several minutes — you may use RO on other tabs without issue.
              </div>

              {/* Mode toggle */}
              <div className="mt-4 flex rounded-md border border-gray-300 overflow-hidden w-fit">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    !mergeModeHistory
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-50`}
                  onClick={() => setMergeModeHistory(false)}
                  disabled={mergeRunning}
                >
                  Current Month
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                    mergeModeHistory
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-50`}
                  onClick={() => setMergeModeHistory(true)}
                  disabled={mergeRunning}
                >
                  History
                </button>
              </div>

              {/* PO system selector */}
              <div className="mt-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Purchase Order System</label>
                <select
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={mergePOSystem}
                  onChange={(e) => setMergePOSystem(e.target.value)}
                  disabled={mergeRunning}
                >
                  <option value="">— None —</option>
                  <option value="shopify">Shopify</option>
                  <option value="stocky">Stocky</option>
                  <option value="faves">Faves</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* History date range — only shown in history mode */}
              {mergeModeHistory ? (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Start Month</label>
                      <DatePicker
                        views={['year', 'month']}
                        label="Start Month"
                        minDate={moment('2000-01-01')}
                        maxDate={moment().subtract(1, 'month')}
                        value={mergeStartMonth}
                        onChange={(newValue: Moment | null) => {
                          if (!newValue) return;
                          setMergeStartMonth(newValue);
                        }}
                        disabled={mergeRunning}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                        inputFormat="MMM YYYY"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">End Month</label>
                      <DatePicker
                        views={['year', 'month']}
                        label="End Month"
                        minDate={moment('2000-01-01')}
                        maxDate={moment().subtract(1, 'month')}
                        value={mergeEndMonth}
                        onChange={(newValue: Moment | null) => {
                          if (!newValue) return;
                          setMergeEndMonth(newValue);
                        }}
                        disabled={mergeRunning}
                        renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                        inputFormat="MMM YYYY"
                      />
                    </div>
                  </LocalizationProvider>
                </div>
              ) : null}

              {/* Merge type checkboxes */}
              <div className="mt-4 space-y-1">

                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={mergeActuals}
                        onChange={(e) => setMergeActuals(e.target.checked)}
                        disabled={mergeRunning}
                      />
                    }
                    label={<span className="text-sm">Merge actuals</span>}
                  />
                  {mergeActuals ? (
                    <div style={{ marginLeft: 32 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={mergeIncludeHardMarkdowns}
                            onChange={(e) => setMergeIncludeHardMarkdowns(e.target.checked)}
                            disabled={mergeRunning}
                          />
                        }
                        label={<span className="text-sm">Include hard markdowns</span>}
                      />
                    </div>
                  ) : null}
                </div>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={mergeOnOrder}
                      onChange={(e) => setMergeOnOrder(e.target.checked)}
                      disabled={mergeRunning || mergeModeHistory}
                    />
                  }
                  label={<span className="text-sm">Merge on orders{mergePOSystem ? ` (${mergePOSystem})` : ''}{' — '}<span className="text-xs text-gray-400">current month only</span></span>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={mergeOnOrderLastMonth}
                      onChange={(e) => setMergeOnOrderLastMonth(e.target.checked)}
                      disabled={mergeRunning || mergeModeHistory}
                    />
                  }
                  label={<span className="text-sm">Merge on orders (last month backdated){mergePOSystem ? ` (${mergePOSystem})` : ''}{' — '}<span className="text-xs text-gray-400">Shopify POs only</span></span>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={mergeReceiving}
                      onChange={(e) => setMergeReceiving(e.target.checked)}
                      disabled={mergeRunning}
                    />
                  }
                  label={<span className="text-sm">Merge receiving{mergePOSystem ? ` (${mergePOSystem})` : ''}</span>}
                />

                {/* Warnings */}
                {mergeOnOrder && mergeModeHistory ? (
                  <div className="ml-8 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠️ Merge on orders is only available for current month.
                  </div>
                ) : null}
                {mergeOnOrderLastMonth && mergeModeHistory ? (
                  <div className="ml-8 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠️ Last month on-order merge is only available for current month runs.
                  </div>
                ) : null}
              </div>

              {mergeRunning ? (
                <div className="mt-4 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                    Running merges...
                  </span>
                </div>
              ) : null}

              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={
                    mergeRunning ||
                    (mergeModeHistory && (!mergeStartMonth || !mergeEndMonth))
                  }
                  onClick={onMergeActuals}
                >
                  {mergeRunning ? 'Running...' : 'Run'}
                </button>
              </div>

              {mergeError ? <div className="mt-2 text-sm text-red-600">{mergeError}</div> : null}
            </div>
          </div>
        ) : null}


        {migrateModalOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => (!migrateRunning ? setMigrateModalOpen(false) : undefined)}
            />
            <div className="relative z-10 w-full max-w-xl rounded-lg bg-white p-4 shadow-lg">
              <div className="flex items-start justify-between gap-3">
                <div className="text-base font-semibold">Shopify Pilot Migration</div>
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                  onClick={() => setMigrateModalOpen(false)}
                  disabled={migrateRunning}
                >
                  Close
                </button>
              </div>

              <div className="mt-3 text-sm text-gray-800">
                {CONFIRM_MESSAGE}
              </div>

              <div className="mt-4">
                <SimpleSearchableClientDropdown
                  clients={filteredClients}
                  selectedClient={selectedTargetClient}
                  setSelectedClient={setSelectedTargetClient}
                  styleOverrides={{ width: '100%' }}
                />
              </div>

              {migrateBrokenSteps.length > 0 && (
                <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3">
                  <div className="font-semibold text-amber-800 mb-1">Some steps failed during migration:</div>
                  <ul className="list-disc pl-5 text-amber-800 text-sm">
                    {migrateBrokenSteps.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-amber-700 mt-2">You may need to manually review or retry these steps.</div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  {migrateRunning ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                      Migrating...
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={migrateRunning || !selectedTargetClient?.Id}
                  onClick={onMigrate}
                >
                  {migrateRunning ? 'Migrating...' : 'Migrate'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {advancedRun.open ? (() => {
          const isCurrentMonth = advancedRun.period.isSame(moment(), 'month');
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black/40"
                onClick={() => (!advancedRun.running ? patchAdvanced({ open: false }) : undefined)}
              />
              <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-4 shadow-lg">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-base font-semibold">Advanced Run</div>
                  <button
                    type="button"
                    className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => patchAdvanced({ open: false })}
                    disabled={advancedRun.running}
                  >
                    Close
                  </button>
                </div>

                <div className="mt-2 text-sm text-gray-700">
                  Select which data steps to run and optionally merge after. Only the chosen steps will execute.
                </div>

                <div className="mt-4">
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      views={['year', 'month']}
                      label="Period"
                      minDate={moment('2000-01-01')}
                      maxDate={moment()}
                      value={advancedRun.period}
                      onChange={(newValue: Moment | null) => {
                        if (!newValue) return;
                        patchAdvanced({ period: newValue });
                      }}
                      disabled={advancedRun.running}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                      inputFormat="MMM YYYY"
                    />
                  </LocalizationProvider>
                </div>

                {/* PO system selector */}
                <div className="mt-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Purchase Order System</label>
                  <select
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={advRunPOSystem}
                    onChange={(e) => setAdvRunPOSystem(e.target.value)}
                    disabled={advancedRun.running}
                  >
                    <option value="">— None —</option>
                    <option value="shopify">Shopify</option>
                    <option value="stocky">Stocky</option>
                    <option value="faves">Faves</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="mt-4 space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Steps</div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.steps.products}
                        onChange={(e) => patchAdvancedSteps({ products: e.target.checked })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Products &amp; Variants</span>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.steps.inventory}
                        onChange={(e) => patchAdvancedSteps({ inventory: e.target.checked })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Inventory</span>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.steps.orders}
                        onChange={(e) => patchAdvancedSteps({ orders: e.target.checked })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Orders (sales, markdowns, returns)</span>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.steps.transfers}
                        onChange={(e) => patchAdvancedSteps({ transfers: e.target.checked })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Transfers</span>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.steps.purchaseOrders}
                        onChange={(e) => patchAdvancedSteps({ purchaseOrders: e.target.checked })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Purchase Orders <span className="text-xs text-gray-400">(Stocky clients only)</span></span>}
                  />
                </div>

                <div className="mt-4 space-y-1">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Merge Operations</div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.merge.actuals}
                        onChange={(e) => patchAdvancedMerge({ actuals: e.target.checked, includeHardMarkdowns: e.target.checked ? advancedRun.merge.includeHardMarkdowns : false })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Merge Actuals</span>}
                  />
                  {advancedRun.merge.actuals ? (
                    <div className="ml-6">
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={advancedRun.merge.includeHardMarkdowns}
                            onChange={(e) => patchAdvancedMerge({ includeHardMarkdowns: e.target.checked })}
                            disabled={advancedRun.running}
                          />
                        }
                        label={<span className="text-sm">Include Hard Markdowns</span>}
                      />
                    </div>
                  ) : null}
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.merge.onOrder}
                        onChange={(e) => patchAdvancedMerge({ onOrder: e.target.checked })}
                        disabled={advancedRun.running || !isCurrentMonth}
                      />
                    }
                    label={
                      <span className="text-sm">
                        Merge On Order{advRunPOSystem ? ` (${advRunPOSystem})` : ''}
                        {!isCurrentMonth ? <span className="ml-1 text-xs text-gray-400">(current month only)</span> : null}
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.merge.onOrderLastMonth}
                        onChange={(e) => patchAdvancedMerge({ onOrderLastMonth: e.target.checked })}
                        disabled={advancedRun.running || !isCurrentMonth}
                      />
                    }
                    label={
                      <span className="text-sm">
                        Merge On Order (Last Month Backdated){advRunPOSystem ? ` (${advRunPOSystem})` : ''}
                        {!isCurrentMonth ? <span className="ml-1 text-xs text-gray-400">(current month only)</span> : null}
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={advancedRun.merge.receiving}
                        onChange={(e) => patchAdvancedMerge({ receiving: e.target.checked })}
                        disabled={advancedRun.running}
                      />
                    }
                    label={<span className="text-sm">Merge Receiving{advRunPOSystem ? ` (${advRunPOSystem})` : ''}</span>}
                  />
                </div>

                {advancedRun.running ? (
                  <div className="mt-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                      Running...
                    </span>
                  </div>
                ) : null}

                <div className="mt-4 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    disabled={advancedRun.running}
                    onClick={onAdvancedRun}
                  >
                    {advancedRun.running ? 'Running...' : 'Run'}
                  </button>
                </div>

                {advancedRun.error ? <div className="mt-2 text-sm text-red-600">{advancedRun.error}</div> : null}
              </div>
            </div>
          );
        })() : null}

        {/* Always show errors inline in job settings even if modal is closed */}
        {migrateError ? <div className="mt-2 text-sm text-red-600">{migrateError}</div> : null}
        {copyError && !copyModalOpen ? <div className="mt-2 text-sm text-red-600">{copyError}</div> : null}
        {mergeError && !mergeModalOpen ? <div className="mt-2 text-sm text-red-600">{mergeError}</div> : null}
        {advancedRun.error && !advancedRun.open ? <div className="mt-2 text-sm text-red-600">{advancedRun.error}</div> : null}
      </div>
    </>
  );
};

export default ShopifyPilotJobSettings;
